import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Scheduling|Appointments|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamAppointments,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateSchedulingAppointments';
export type SPCandidateSchedulingAppointmentsLocNames =
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePrevious`
	| `${LocNameStart}TitleUpcoming`
	| `${LocNameStart}ContentPendingAccommodations`
	| `${LocNameStart}ButtonHidePrevious`
	| `${LocNameStart}ButtonShowPrevious`
	| `${LocNameStart}LabelDate`
	| `${LocNameStart}LabelExamCode`
	| `${LocNameStart}LabelExamName`
	| `${LocNameStart}LabelDeliveryProvider`
	| `${LocNameStart}LabelRegistrationNumber`
	| `${LocNameStart}ImpersonateError`
	| `${LocNameStart}LabelPromoCode`
	| `${LocNameStart}LabelVoucherNumber`;

export const spCandidateSchedulingAppointmentsLocs: LocDefs<SPCandidateSchedulingAppointmentsLocNames> =
	{
		SPCandidateSchedulingAppointmentsTitleTab: {
			defaultValue: 'Appointments',
			description: 'Tab Title',
			displayPath: `${pathStart}Tab Title`,
			displayType: 'Title',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.Title.Tab',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsTitlePrevious: {
			defaultValue: 'Previous Appointments',
			description: 'Previous Appointments Section Title',
			displayPath: `${pathStart}Previous Appointments`,
			displayType: 'Title',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.Title.Previous',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsTitleUpcoming: {
			defaultValue: 'Upcoming Appointments',
			description: 'Upcoming Appointments Section Title',
			displayPath: `${pathStart}Upcoming Appointments`,
			displayType: 'Title',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.Title.Upcoming',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsContentPendingAccommodations: {
			defaultValue:
				'The pending accommodations on file must be reviewed before this candidate can schedule for any exam.',
			description: 'Message shown when candidate has pending accommodations',
			displayPath: `${pathStart}Pending Accommodations`,
			displayType: 'Content',
			legacyName: 'Exam.Accommodations/Candidates.Scheduling.Content.PendingAccom',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsButtonHidePrevious: {
			defaultValue: 'Hide Previous Appointments',
			description: 'Button to hide previous appointments',
			displayPath: `${pathStart}Hide Previous`,
			displayType: 'Button',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.Button.HidePrevious',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsButtonShowPrevious: {
			defaultValue: 'Show Previous Appointments',
			description: 'Button to show previous appointments',
			displayPath: `${pathStart}Show Previous`,
			displayType: 'Button',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.Button.ShowPrevious',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsLabelDate: {
			defaultValue: 'Appointment Date',
			description: 'Column header for Appointment Date',
			displayPath: `${pathStart}Appointment Date`,
			displayType: 'Label',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.ColumnHeading.Date',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsLabelExamCode: {
			defaultValue: 'Exam Code',
			description: 'Column header for Exam Code',
			displayPath: `${pathStart}Exam Code`,
			displayType: 'Label',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.ColumnHeading.ExamCode',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsLabelExamName: {
			defaultValue: 'Exam Name',
			description: 'Column header for Exam Name',
			displayPath: `${pathStart}Exam Name`,
			displayType: 'Label',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.ColumnHeading.ExamName',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsLabelDeliveryProvider: {
			defaultValue: 'Delivery Provider',
			description: 'Column header for Delivery Provider',
			displayPath: `${pathStart}Delivery Provider`,
			displayType: 'Label',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.ColumnHeading.DeliveryProvider',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsLabelRegistrationNumber: {
			defaultValue: 'Registration number',
			description: 'Column header for Registration number',
			displayPath: `${pathStart}Registration number`,
			displayType: 'Label',
			legacyName: 'Exam.Appointments/Candidates.Scheduling.ColumnHeading.Registration',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsImpersonateError: {
			defaultValue: 'Error impersonating candidate',
			description: 'Error message for impersonating a candidate',
			displayPath: `${pathStart}Impersonate Error`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsLabelPromoCode: {
			defaultValue: 'Promo Code',
			description: 'Column header for Promo Code',
			displayPath: `${pathStart}Promo Code`,
			displayType: 'Label',			
			...sharedProperties,
		},
		SPCandidateSchedulingAppointmentsLabelVoucherNumber: {
			defaultValue: 'Voucher Number',
			description: 'Column header for Voucher Number',
			displayPath: `${pathStart}Voucher Number`,
			displayType: 'Label',
			...sharedProperties,
		},
	};

export type SPCandidateSchedulingAppointmentsLocType =
	LocMap<SPCandidateSchedulingAppointmentsLocNames>;
